import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import useOrganizationService from '../../../../app/services/hooks/useOrganizationService';
import useHandleErrorFields from '../../../../hooks/useHandleErrorFields';
import useReactConfirmAlert from '../../../../hooks/useReactConfirmAlert';
import DefaultCard from '../../../../components/default/DefaultCard';
import DefaultModal from '../../../../components/default/DefaultModal';
import ButtonGeneral from '../../../../components/buttons/ButtonGeneral';
import ButtonTableAction from '../../../../components/buttons/ButtonTableAction';
import ButtonSaveForm from '../../../../components/buttons/ButtonSaveForm';
import InputField from '../../../../components/InputField';
import TextAreaField from '../../../../components/TextAreaField';
import SquareCheckbox from '../../../../components/SquareCheckbox/SquareCheckbox';
import { BuiltinRole, CapabilityItem, OrganizationRole } from '../../types';
import { OrganizationContextProps } from '../index';

interface RoleForm {
    name: string;
    description: string;
    capabilities: string[];
    can_be_assigned: boolean;
}

const EMPTY_ROLE: RoleForm = { name: '', description: '', capabilities: [], can_be_assigned: true };

/**
 * Roles propios del estudio: el administrador arma roles a partir del catálogo
 * de permisos que Monere permite delegar (lo exclusivo del administrador no se delega).
 */
const RolesTab: React.FC<OrganizationContextProps> = ({ organization }) => {
    const { getRoles, storeRole, updateRole, deleteRole, fetchingRoles, fetchingSaveRole } = useOrganizationService();
    const { fieldErrorMessage, onFocusRemove, setErrors } = useHandleErrorFields();
    const { requestConfirmation } = useReactConfirmAlert();

    const [builtin, setBuiltin] = useState<BuiltinRole[]>([]);
    const [roles, setRoles] = useState<OrganizationRole[]>([]);
    const [catalog, setCatalog] = useState<CapabilityItem[]>([]);
    const [canManageRoles, setCanManageRoles] = useState(false);
    const [editing, setEditing] = useState<OrganizationRole | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState<RoleForm>(EMPTY_ROLE);

    const canManage = canManageRoles && organization.status === 'active';

    const load = () => {
        getRoles({
            onSuccess: (response) => {
                setBuiltin(response.data.builtin || []);
                setRoles(response.data.roles || []);
                setCatalog(response.data.catalog || []);
                setCanManageRoles(!!response.data.can_manage);
            },
            onError: (response) => toast.error(response.message)
        });
    };

    useEffect(() => {
        load();
    }, []);

    // Catálogo agrupado por 'group' respetando el orden del backend
    const groups = useMemo(() => {
        const result: { group: string; items: CapabilityItem[] }[] = [];
        catalog.forEach((item) => {
            let entry = result.find((g) => g.group === item.group);
            if (!entry) {
                entry = { group: item.group, items: [] };
                result.push(entry);
            }
            entry.items.push(item);
        });
        return result;
    }, [catalog]);

    const capabilityLabel = (key: string) => catalog.find((item) => item.key === key)?.label || key;

    const openCreate = () => {
        setErrors({});
        setEditing(null);
        setForm(EMPTY_ROLE);
        setShowForm(true);
    };

    const openEdit = (role: OrganizationRole) => {
        setErrors({});
        setEditing(role);
        setForm({
            name: role.name,
            description: role.description || '',
            capabilities: [...role.capabilities],
            can_be_assigned: role.can_be_assigned
        });
        setShowForm(true);
    };

    const toggleCapability = (key: string, checked: boolean) => {
        onFocusRemove('capabilities');
        setForm((current) => ({
            ...current,
            capabilities: checked
                ? Array.from(new Set([...current.capabilities, key]))
                : current.capabilities.filter((capability) => capability !== key)
        }));
    };

    const handleSave = () => {
        const events = {
            onSuccess: (response: any) => {
                toast.success(response.message);
                setErrors({});
                setShowForm(false);
                load();
            },
            onError: (response: any) => toast.error(response.message),
            onFieldError: (response: any) => {
                setErrors(response.data);
                const first = Object.values(response.data || {})[0] as string[] | undefined;
                toast.error(first?.[0] || 'Revisa los datos del rol');
            }
        };
        if (editing) updateRole(editing.id, { ...form }, events);
        else storeRole({ ...form }, events);
    };

    const handleDelete = (role: OrganizationRole) => {
        requestConfirmation({
            title: `¿Borrar el rol "${role.name}"?`,
            message: role.members_count > 0
                ? `Hay ${role.members_count} persona(s) con este rol: cámbiales el rol antes de borrarlo.`
                : 'El rol dejará de estar disponible para asignar.',
            buttons: {
                confirmButton: {
                    label: 'Sí, borrar',
                    onClick: () =>
                        deleteRole(role.id, {
                            onSuccess: (response) => {
                                toast.success(response.message);
                                load();
                            },
                            onError: (response) => toast.error(response.message)
                        })
                },
                cancelButton: { label: 'Cancelar' }
            }
        });
    };

    return (
        <DefaultCard>
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
                <div style={{ minWidth: 260, flex: 1 }}>
                    <h5 className="mb-1">Roles del estudio</h5>
                    <p className="text-muted small mb-0">
                        Además de los roles base, crea roles a la medida eligiendo qué puede hacer cada persona.
                        La administración del equipo, la marca, los correos y la suscripción quedan siempre en manos del administrador.
                    </p>
                </div>
                {canManage && <ButtonGeneral onClick={openCreate} title="Nuevo rol" icon="fa-plus" size="small" />}
            </div>

            <h6 className="text-muted text-uppercase small mt-2">Roles base</h6>
            <div className="table-responsive mb-4">
                <table className="table align-middle mb-0">
                    <tbody>
                        {builtin.map((role) => (
                            <tr key={role.value}>
                                <td style={{ width: '30%' }}><span className="fw-bold">{role.name}</span></td>
                                <td><small className="text-muted">{role.description}</small></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <h6 className="text-muted text-uppercase small">Roles propios</h6>
            {fetchingRoles && roles.length === 0 ? (
                <div className="text-center py-4"><div className="spinner-border text-primary" /></div>
            ) : roles.length === 0 ? (
                <p className="text-muted small">Aún no hay roles propios.</p>
            ) : (
                <div className="table-responsive">
                    <table className="table table-hover align-middle" data-testid="roles-table">
                        <thead>
                            <tr>
                                <th>Rol</th>
                                <th>Permisos</th>
                                <th className="text-center">Personas</th>
                                <th className="text-center">Lleva casos</th>
                                {canManage && <th className="text-center">Acciones</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {roles.map((role) => (
                                <tr key={role.id}>
                                    <td>
                                        <span className="fw-bold d-block">{role.name}</span>
                                        {role.description && <small className="text-muted">{role.description}</small>}
                                    </td>
                                    <td>
                                        <small className="text-muted">
                                            {role.capabilities.length
                                                ? role.capabilities.map(capabilityLabel).join(' · ')
                                                : 'Solo acceso básico'}
                                        </small>
                                    </td>
                                    <td className="text-center">{role.members_count}</td>
                                    <td className="text-center">
                                        {role.can_be_assigned
                                            ? <i className="fas fa-check text-success" title="Se le pueden asignar marcas" />
                                            : <i className="fas fa-minus text-muted" title="No lleva casos" />}
                                    </td>
                                    {canManage && (
                                        <td className="text-center">
                                            <div className="d-flex justify-content-center gap-1">
                                                <ButtonTableAction callbackFunction={() => openEdit(role)} title="Editar"
                                                                   classIcon="fa-pen" colorIcon="text-warning" sizeIcon="16" />
                                                <ButtonTableAction callbackFunction={() => handleDelete(role)} title="Borrar"
                                                                   classIcon="fa-trash" colorIcon="text-danger" sizeIcon="16" />
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <DefaultModal
                title={editing ? `Editar rol "${editing.name}"` : 'Nuevo rol'}
                show={showForm}
                size="lg"
                handleClose={() => setShowForm(false)}
                actionButton={<ButtonSaveForm callbackSave={handleSave} loading={fetchingSaveRole} title="Guardar" />}
            >
                <div className="row">
                    <div className="col-12 mb-3">
                        <InputField label="Nombre" value={form.name} required
                                    onChange={(value) => setForm({ ...form, name: value })}
                                    onFocus={() => onFocusRemove('name')} error={fieldErrorMessage('name')} />
                    </div>
                    <div className="col-12 mb-3">
                        <TextAreaField label="Descripción" value={form.description} rows={2}
                                       onChange={(value) => setForm({ ...form, description: value })}
                                       onFocus={() => onFocusRemove('description')} error={fieldErrorMessage('description')} />
                    </div>
                    <div className="col-12 mb-3">
                        <div className="form-check form-switch">
                            <input className="form-check-input" type="checkbox" id="role_can_be_assigned"
                                   checked={form.can_be_assigned}
                                   onChange={(e) => setForm({ ...form, can_be_assigned: e.target.checked })} />
                            <label className="form-check-label" htmlFor="role_can_be_assigned">
                                Puede llevar casos (se le pueden asignar marcas)
                            </label>
                        </div>
                    </div>
                    <div className="col-12">
                        <label className="fw-bold mb-2">Permisos</label>
                        {fieldErrorMessage('capabilities') && (
                            <small className="text-danger d-block mb-2">{fieldErrorMessage('capabilities')}</small>
                        )}
                        {groups.map(({ group, items }) => (
                            <div key={group} className="mb-3">
                                <h6 className="text-muted small text-uppercase mb-2">{group}</h6>
                                {items.map((item) => (
                                    <SquareCheckbox
                                        key={item.key}
                                        id={`capability_${item.key}`}
                                        name="capabilities"
                                        value={item.key}
                                        checked={form.capabilities.includes(item.key)}
                                        onChange={(_v: string, checked: boolean) => toggleCapability(item.key, checked)}
                                        label={item.label}
                                        className="mb-1"
                                    />
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </DefaultModal>
        </DefaultCard>
    );
};

export default RolesTab;
